import React, { useState } from 'react';
import { Checkbox } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { Images } from 'utils';
import { SelectTypeAutocomplete } from 'components';
import { goalTypes } from 'fragments';
import {
    buildManualSessionDataColumn,
    buildSessionDataColumns,
    buildSessionDataTrialColumns,
    getSessionDataColumnHeader,
    getSessionDataGoalTypes,
    getSessionDataTrialColumnHeader,
    isManualSessionDataColumn,
    sessionDataColumnList,
    sessionDataManualInputEnums,
    sessionDataManualInputList,
    sessionDataTrialColumnList,
} from '../../sessionDataTable.constants';
import { PencilIcon } from '../../formBody/elements/sessionDataTableElement';
import { OptionInput } from './tableColumnsSection';

const BoltIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
        <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
);

/**
 * One column in a COLUMNS list. AUTO columns show only their field;
 * MANUAL columns carry everything staff need to fill them — a name, the input
 * type, whether an answer is required, and a dropdown's options.
 */
const ColumnCard = ({ col, label, canRemove, onChange, onRemove }) => {
    const isManual = isManualSessionDataColumn(col);
    const options = col.options || [];
    const setOptions = (next) => onChange({ options: next });

    return (
        <div className={`session-data-column-card ${isManual ? 'manual' : ''}`}>
            <div className="session-data-column-card-head">
                <span className={`session-data-kind-badge ${isManual ? 'manual' : 'auto'}`}>
                    {isManual ? <PencilIcon /> : <BoltIcon />}
                    {isManual ? 'Manual' : 'Auto'}
                </span>
                {isManual ? (
                    <OptionInput value={col.name} onChange={(name) => onChange({ name })} placeholder="Column name" />
                ) : (
                    <span className="session-data-column-card-name">{label}</span>
                )}
                {canRemove && (
                    <button type="button" className="session-data-column-card-remove" title="Remove column" onClick={onRemove}>
                        <img src={Images.closeSingleVector} alt="remove" />
                    </button>
                )}
            </div>

            {isManual && (
                <>
                    <div className="session-data-column-card-row">
                        <SelectTypeAutocomplete
                            disableClearable={true}
                            name={`sessionDataManualInput-${col.id}`}
                            handleSelect={(event) => onChange({ inputType: event.target.value })}
                            defaultValue={col.inputType || sessionDataManualInputEnums.TEXT}
                            list={sessionDataManualInputList}
                            renderValue={(i) => i?.name}
                            uiType={'tableFilter'}
                            noError={true}
                        />
                        <label className="session-data-column-card-required">
                            <Checkbox
                                checked={!!col.required}
                                onChange={(e) => onChange({ required: e.target.checked })}
                                style={{ padding: 0, color: col.required ? '#0C66E4' : '#D0D5DD' }}
                            />
                            <span>Required</span>
                        </label>
                    </div>

                    {col.inputType === sessionDataManualInputEnums.DROPDOWN && (
                        <div className="table-props-options-list">
                            {options.map((opt, i) => (
                                <div key={opt.id} className="table-props-option-row">
                                    <OptionInput
                                        value={opt.name}
                                        onChange={(value) => setOptions(options.map((o) => (o.id === opt.id ? { ...o, name: value } : o)))}
                                        placeholder={`Option ${i + 1}`}
                                    />
                                    <div
                                        className="table-props-option-remove"
                                        onClick={() => setOptions(options.filter((o) => o.id !== opt.id))}
                                    >
                                        <img src={Images.closeSingleVector} alt="remove option" />
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="add-option-btn"
                                onClick={() => setOptions([...options, { id: uuidv4(), name: '' }])}
                            >
                                <img src={Images.addBluePlus} alt="add" />
                                <span>Add Option</span>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const COLUMN_SCOPES = {
    summary: { key: 'columns', catalog: sessionDataColumnList, build: buildSessionDataColumns, header: getSessionDataColumnHeader },
    trial: {
        key: 'trialColumns',
        catalog: sessionDataTrialColumnList,
        build: buildSessionDataTrialColumns,
        header: (col) => getSessionDataTrialColumnHeader(col),
    },
};

/**
 * The column list of one table — the summary rows or the per-trial breakdown.
 * **Add column** offers the scope's session fields not already shown; **Manual
 * column** adds a staff-filled one. The last column cannot be removed: a table
 * with none has nothing to render.
 */
const ColumnsEditor = ({ title, hint, scope, config, patchConfig }) => {
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const { key, catalog, build, header } = COLUMN_SCOPES[scope];
    const columns = config[key] || [];
    const unusedFields = catalog.filter((option) => !columns.some((col) => col.field === option.field));

    const setColumns = (next, extra = {}) => patchConfig({ [key]: next, ...extra });
    const updateColumn = (id, patch) => setColumns(columns.map((col) => (col.id === id ? { ...col, ...patch } : col)));
    const removeColumn = (id) =>
        setColumns(
            columns.filter((col) => col.id !== id),
            config._selectedColumnId === id ? { _selectedColumnId: null, _selectedColumnScope: null } : {}
        );
    const addColumn = (column) => {
        setAddMenuOpen(false);
        setColumns([...columns, column], { _selectedColumnId: column.id, _selectedColumnScope: scope });
    };

    return (
        <div className="properties-section">
            <div className="section-header">
                <img src={Images.layoutIcon} alt="columns" />
                <span>{title}</span>
            </div>

            <div className="properties-info-box blue">
                <img src={Images.infoIcon} alt="info" className="session-data-props-info-icon" />
                <span className="properties-info-text">{hint}</span>
            </div>

            <div className="session-data-column-cards">
                {columns.map((col) => (
                    <ColumnCard
                        key={col.id}
                        col={col}
                        label={header(col)}
                        canRemove={columns.length > 1}
                        onChange={(patch) => updateColumn(col.id, patch)}
                        onRemove={() => removeColumn(col.id)}
                    />
                ))}
            </div>

            <div className="session-data-add-column">
                <button
                    type="button"
                    className="session-data-add-column-btn"
                    disabled={!unusedFields.length}
                    onClick={() => setAddMenuOpen((open) => !open)}
                >
                    <img src={Images.addBluePlus} alt="add" />
                    <span>Add column</span>
                </button>
                {addMenuOpen && (
                    <div className="session-data-field-picker session-data-add-column-menu">
                        {unusedFields.map((option) => (
                            <button
                                type="button"
                                key={option.field}
                                className="session-data-field-picker-option"
                                onClick={() => addColumn(build([option.field])[0])}
                            >
                                <span>{option.name}</span>
                            </button>
                        ))}
                    </div>
                )}
                <button type="button" className="session-data-manual-column-btn" onClick={() => addColumn(buildManualSessionDataColumn())}>
                    <PencilIcon />
                    <span>Manual column</span>
                </button>
            </div>
        </div>
    );
};

/**
 * Properties panel for the Session Data Table block.
 *
 * Three things are configurable: whether the per-trial breakdown is nested
 * under each data row, which goal types' rows the table includes (the api
 * filters on it when it fills the note), and which session-data field the
 * currently selected column binds to. The column is selected by clicking its header on the canvas, which
 * parks its id on `properties.sessionDataTable._selectedColumnId` — the same
 * transient-selection handshake the free-form TABLE attribute uses via
 * `table._selectedCellInfo`.
 *
 * The COLUMNS lists manage each table's columns — the summary rows and, when
 * trial details are on, the per-trial breakdown: AUTO columns bind to a
 * session-data field, MANUAL columns are filled in by staff for each row / trial.
 */
export const SessionDataTableSection = ({ selectedItem, handleUpdate }) => {
    const config = selectedItem?.properties?.sessionDataTable || {};
    const isTrialScope = config._selectedColumnScope === 'trial';
    const catalog = isTrialScope ? sessionDataTrialColumnList : sessionDataColumnList;
    const columns = (isTrialScope ? config.trialColumns : config.columns) || [];
    const selectedColumn = columns.find((col) => col.id === config._selectedColumnId) || null;

    const patchConfig = (patch) => handleUpdate('sessionDataTable', { ...config, ...patch });

    const selectedGoalTypes = getSessionDataGoalTypes(config);
    // At least one type stays selected — a table that includes no goal type
    // could never show a row.
    const toggleGoalType = (type) => {
        const isSelected = selectedGoalTypes.includes(type);
        if (isSelected && selectedGoalTypes.length === 1) return;
        const next = isSelected ? selectedGoalTypes.filter((t) => t !== type) : [...selectedGoalTypes, type];
        patchConfig({ goalTypes: goalTypes.map((opt) => opt.id).filter((id) => next.includes(id)) });
    };

    const handleFieldChange = (field) => {
        if (!selectedColumn) return;
        const key = isTrialScope ? 'trialColumns' : 'columns';
        patchConfig({
            [key]: columns.map((col) => (col.id === selectedColumn.id ? { ...col, field } : col)),
        });
    };

    // Fields taken by sibling columns are hidden — the same field twice would
    // render two identical columns.
    const availableFields = catalog.filter(
        (option) => option.field === selectedColumn?.field || !columns.some((col) => col.field === option.field)
    );

    return (
        <>
            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.layoutIcon} alt="display options" />
                    <span>DISPLAY OPTIONS</span>
                </div>

                <div className="behavior-row">
                    <div className="behavior-info">
                        <span className="behavior-title">Include trial details</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!config.includeTrialDetails}
                            onChange={(e) => patchConfig({ includeTrialDetails: e.target.checked })}
                        />
                        <span className="slider" />
                    </label>
                </div>
            </div>

            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.layoutIcon} alt="goal types" />
                    <span>GOAL TYPES</span>
                </div>

                {goalTypes.map((opt) => {
                    const checked = selectedGoalTypes.includes(opt.id);
                    return (
                        <div key={opt.id} className="behavior-row">
                            <div className="behavior-info">
                                <span className="behavior-title">{opt.label}</span>
                            </div>
                            <Checkbox
                                checked={checked}
                                disabled={checked && selectedGoalTypes.length === 1}
                                onChange={() => toggleGoalType(opt.id)}
                                style={{ padding: 0, color: checked ? '#0C66E4' : '#D0D5DD' }}
                            />
                        </div>
                    );
                })}
            </div>

            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.contentIcon} alt="selected column" />
                    <span>SELECTED COLUMN CONTENT</span>
                </div>

                {selectedColumn && isManualSessionDataColumn(selectedColumn) ? (
                    <p className="session-data-props-hint">A manual column is set up in the COLUMNS list below.</p>
                ) : selectedColumn ? (
                    <div className="props-input-group">
                        <div className="props-label">Label</div>
                        <SelectTypeAutocomplete
                            disableClearable={true}
                            name={`sessionDataColumn-${selectedColumn.id}`}
                            handleSelect={(event) => handleFieldChange(event.target.value)}
                            defaultValue={selectedColumn.field}
                            list={availableFields}
                            renderValue={(i) => i?.name}
                            uiType={'tableFilter'}
                            noError={true}
                        />
                    </div>
                ) : (
                    <p className="session-data-props-hint">Select a column on the table to change what it shows.</p>
                )}

                <div className="properties-info-box blue">
                    <img src={Images.infoIcon} alt="info" className="session-data-props-info-icon" />
                    <span className="properties-info-text">
                        Only collected Session Data fields can be included. Report values populate automatically when a note is created.
                    </span>
                </div>
            </div>

            <div className="properties-divider" />

            <ColumnsEditor
                title="COLUMNS"
                hint="Auto columns fill from collected session data when a note is created. Manual columns are filled in by staff for each goal row."
                scope="summary"
                config={config}
                patchConfig={patchConfig}
            />

            {config.includeTrialDetails && (
                <>
                    <div className="properties-divider" />
                    <ColumnsEditor
                        title="TRIAL COLUMNS"
                        hint="Auto columns fill from each collected trial. Manual columns are filled in by staff for each trial."
                        scope="trial"
                        config={config}
                        patchConfig={patchConfig}
                    />
                </>
            )}
        </>
    );
};
